<template>
    <span class="radio-wrapper">
        <div :class="classList">
            <span :id="groupLegendId"
                  class="form__radio-list__intro">{{ introText }}</span>
            <slot />
            <ul :id="groupId" role="radiogroup" :aria-labelledby="groupLegendId"
                class="form__radio-list__options">
                <li v-for="item in options"
                    :key="item.key ? item.key : item"
                    class="form__radio-list__options__item">
                    <input type="radio"
                           :id="getId(item)"
                           :value="item.value != undefined ? item.value : item"
                           :disabled="disabled"
                           :checked="isChecked(item)"
                           :tabindex="-1"
                           @input="onInput"
                           @change="onChange"
                           @keydown="$emit('keydown', $event)"
                           @keypress="$emit('keypress', $event)"
                           @keyup="$emit('keyup', $event)"
                           @keyup.enter="$emit('keyup.enter', $event)"
                           @keyup.space="$emit('keyup.space', $event)"
                           @mousedown="$emit('mousedown', $event)"
                           @mouseover="$emit('mouseover', $event)"
                           @mouseup="$emit('mouseup', $event)"
                           @touchstart="$emit('touchstart', $event)"
                           @touchend="$emit('touchend', $event)"
                           @touchcancel="$emit('touchcancel', $event)">
                    <label :id="getLabelId(item)"
                           :for="getId(item)"
                           :tabindex="0"
                           @keyup.enter="onLabelEnterOrSpace"
                           @keyup.space="onLabelEnterOrSpace">{{ item.text ? item.text : item }}</label>
                </li>
            </ul>
        </div>
    </span>
</template>

<script>

// NOTE: Do NOT attempt to add a 'name' tag to a radio input in vue - Vue handles groupings itself and 'name' will
// break data selecton.
// https://stackoverflow.com/questions/57458819/how-to-check-a-radio-button-in-vue-js-2-without-using-v-model

import _ from 'lodash'

import Casing from '@/helpers/casing'

import FormControlInput from './form-control-input'

export default {

    props: {
        groupName: {
            type: String,
            default: '',
        },
        introText: {
            type: String,
            default: '',
        },
        options: {
            type: Array,
            required: true,
        },
        horizontalLayout: {
            type: Boolean,
            default: false,
        },
    },

    mixins: [
        FormControlInput,
    ],

    data() {
        return {
            keyupStarted: false,
        }
    },

    computed: {
        classList() {
            const classes = [ 'form__radio-list' ]

            if (this.def)
                classes.push(`form__radio-list--${ this.def } --${ this.def } js-${ this.def }`)

            if (this.horizontalLayout)
                classes.push('form__radio-list--horizontal')

            return classes.join(' ')
        },

        groupId() {
            return Casing.kebabCase(this.groupName || this.def || this.introText)
        },

        groupLegendId() {
            return `${ this.groupId }-legend`
        },
    },

    methods: {

        /**
         * Gets an option item from a key.
         *
         * @param {Object} item The item from the options array.
         */
        _getOption(id) {
            const validOptions = this.options.filter(i => id == this.getId(i))
            return validOptions.length ? validOptions[0] : null
        },

        /**
         * Relocates the event to the radio.
         *
         * @param {Object} $event The event object.
         */
        _relocateEvent($event) {
            const mappedControlId = $event.target && $event.target.getAttribute('for')
            const mappedOption = this._getOption(mappedControlId)

            if (mappedOption) {
                this.internalValue = this.formatEventOutput(mappedOption.value)
                this.$emit('input', this.internalValue)
            }

            _.delay(this._resetStackState, 100, this)
        },

        /**
         * Resets the stack state to prevent overflows.
         *
         * @param {Vue} component The Vue component (this)
         */
        _resetStackState(component) {
            component.keyupStarted = false
        },

        /**
         * Formats the output of event values. This can be overridden to format for specific control types.
         *
         * @param {any} value
         * @returns {[Boolean, Number]} The formatted output according to the control.
         */
        formatEventOutput(value) {
            const intVal = parseInt(value)
            return !isNaN(intVal)
                ? intVal
                : value === 'true'
                    ? true
                    : value === 'false'
                        ? false
                        : value
        },

        /**
         * Gets the id for an item.
         *
         * @param {Object} item The item from the options array.
         */
        getId(item) {
            return (this.def ? `${ this.def }-` : '') + Casing.kebabCase(item.key ? item.key : item)
        },

        /**
         * Gets the id for an item label.
         *
         * @param {Object} item The item from the options array.
         */
        getLabelId(item) {
            return `${ this.getId(item) }-label`
        },

        /**
         * Indicates whether the radio item should be checked or not.
         *
         * @param {Object} item The item from the options array.
         */
        isChecked(item) {
            return item.value == this.internalValue || item == this.internalValue
        },

        /**
         * Event that fires on change of the control value.
         *
         * Debouncing: This is debounced to ensure that change fires after input.
         *
         * @param {Object} ev The event data.
         */
        onChange: _.debounce(function(ev) {
            /* eslint-disable no-invalid-this */
            this.$emit('change', ev)
            if (!this.inputTriggers) {
                this.internalValue = this.formatEventOutput(ev.target.value)
                this.$emit('input', this.internalValue)
            }
            /* eslint-enable no-invalid-this */
        }, 100),

        /**
         * Event that fires on inputing into a control.
         *
         * Debouncing: This is debounced to minimise triggers for an average typing speed.
         * Browser Note: Some browsers (EdgeHTML) do not trigger input events for some controls. Where this is
         * the case the 'change' event will fire an input event if input has not fired.
         *
         * @param {Object} ev The event data.
         */
        onInput(ev) {
            this.inputTriggers = true
            this.internalValue = this.formatEventOutput(ev.target.value)
            this.$emit('input', this.internalValue)
        },

        /**
         * Event that fires on pressing enter or space on the label part of the control.
         *
         * @param {Object} $event The event data.
         */
        onLabelEnterOrSpace($event) {
            if (!this.keyupStarted) {
                this.keyupStarted = true
                this._relocateEvent($event)
            }
        },
    },
}

</script>

<style lang="scss">

.form {

    @include element('radio-list') {

        @include element('options') {
            line-height: 1.8em;
            @include unstyle-list;

            @include element('item') {
                display: block;

                input {
                    margin-left: 1.5em;
                    margin-right: 0.5em;
                }

                input, label {
                    vertical-align: middle;
                }

                label {
                    @include user-select-none;
                }
            }
        }

        @include modifier('horizontal') {
            .form__radio-list__options__item {
                display: inline-block;
            }
        }
    }
}

</style>
